import { useTranslation } from "react-i18next";
import type { Row } from "shared/react-table/appTable";
import { TableCell, TableRow } from "components/ui/table";
import { TableStatusBadges } from "components/GenerationStatusBadge/TableStatusBadges";
import { SearchProductWithScore } from "hooks/search/types";
import { BooksTableDetailRowDescriptionSnippet } from "./BooksTableDetailRowDescriptionSnippet";
import { BooksTableDetailRowDetailField } from "./BooksTableDetailRowDetailField";
import { BooksTableDetailRowGenreBadges } from "./BooksTableDetailRowGenreBadges";
import { BooksTableDetailRowKeywordBadges } from "./BooksTableDetailRowKeywordBadges";

export const BooksTableDetailRow = ({
  product,
  colSpan,
  row,
}: {
  product: SearchProductWithScore;
  colSpan: number;
  row: Row<SearchProductWithScore>;
}) => {
  const { t } = useTranslation(["components"]);

  const { publishers } = product;
  const publisherName =
    publishers && publishers.length > 0
      ? publishers.map((p) => p.name).join(", ")
      : product.publisher || "—";

  const imprintName = product.imprints?.join(", ") || "—";

  const publishedAt = product.publishedAt
    ? new Date(product.publishedAt).toISOString().slice(0, 10)
    : "—";

  const description = product.description ?? "";
  const keywords = product.keywords ?? [];
  const genres = product.genre ?? [];
  const wordCount = description.split(/\s+/).filter(Boolean).length;

  return (
    <TableRow
      id={`detail-row-${product.id}`}
      className="border-b border-secondary-100 hover:bg-transparent"
      data-testid={`detail-row-${product.id}`}
    >
      <TableCell className="bg-secondary-100/50 py-4" />

      <TableCell
        colSpan={colSpan - 1}
        className="bg-secondary-100/50 py-4 pr-6 whitespace-normal"
      >
        <div className="border-l border-secondary-100 pl-8 py-2 space-y-4 ml-[11px]">
          <div className="flex flex-wrap items-start gap-x-14 gap-y-3">
            <BooksTableDetailRowDetailField
              label={t("components:booksTable.columns.publishedBy")}
              value={publisherName}
            />
            <BooksTableDetailRowDetailField
              label={t("components:booksTable.columns.imprint")}
              value={imprintName}
            />
            <BooksTableDetailRowDetailField
              label={t("components:booksTable.columns.publishedOn")}
              value={publishedAt}
            />
            <BooksTableDetailRowDetailField
              label={t("components:booksTable.columns.generated")}
            >
              <TableStatusBadges row={row} fetch />
            </BooksTableDetailRowDetailField>
          </div>

          <div className="border-t border-secondary-100" />

          <div className="flex flex-col gap-4">
            <BooksTableDetailRowGenreBadges genres={genres} />
            <BooksTableDetailRowKeywordBadges keywords={keywords} />
            <BooksTableDetailRowDescriptionSnippet
              description={description}
              wordCount={wordCount}
            />
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};
